import { useEffect } from 'react';
import { useSnackbar } from 'notistack';
// next
import NextLink from 'next/link';
import { useRouter } from 'next/router';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import { Box, Stack, Link, Container, Typography } from '@mui/material';
// routes
import { PATH_AUTH } from '../../routes/paths';
// guards
import RegisterGuard from '../../guards/RegisterGuard';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import Page from '../../components/Page';
import Image from '../../components/Image';
// sections
import { LoginForm } from '../../sections/auth/login';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const HeaderStyle = styled('header')(({ theme }) => ({
  top: 0,
  zIndex: 9,
  lineHeight: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  padding: theme.spacing(3),
  justifyContent: 'space-between',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    padding: theme.spacing(7, 5, 0, 7),
  },
}));

const SectionStyle = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: 464,
  maxHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: theme.palette.primary.main,
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0),
}));

// ----------------------------------------------------------------------

export default function Login() {
  const smUp = useResponsive('up', 'sm');

  const mdUp = useResponsive('up', 'md');

  const theme = useTheme();

  const { isReady, query } = useRouter();
  const { reason } = query;

  const { enqueueSnackbar } = useSnackbar();

  // Set by the axios interceptor when a 401 forced a logout (see utils/axios).
  // On a static export the first render has an empty query (and isReady is
  // already true because _app has getInitialProps); Next fills the query from
  // location.search right after mount, so react to `reason` itself.
  useEffect(() => {
    if (isReady && reason === 'expired') {
      enqueueSnackbar('로그인이 만료되어 다시 로그인해 주세요', { variant: 'warning' });
    }
  }, [isReady, reason, enqueueSnackbar]);

  return (
    <RegisterGuard>
      <Page title="로그인">
        <RootStyle>
          <HeaderStyle>
            <div />
            {smUp && (
              <Typography variant="body2" sx={{ mt: { md: -2 } }}>
                계정이 없으신가요? {''}
                <NextLink href={PATH_AUTH.register} passHref>
                  <Link variant="subtitle2">가입하기</Link>
                </NextLink>
              </Typography>
            )}
          </HeaderStyle>

          {mdUp && (
            <SectionStyle>
              <Image
                visibleByDefault
                disabledEffect
                src="https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/crew-side.png"
                alt="login"
                sx={{ cursor: 'pointer' }}
                onClick={() => window.open("https://www.dio.so", '_self', 'noopener, noreferrer')}
              />
            </SectionStyle>
          )}

          <Container maxWidth="sm">
            <ContentStyle>
              <Stack direction="row" alignItems="center" sx={{ mb: 4 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h4" gutterBottom>
                    <Link underline="none" href="https://www.dio.so" sx={{ fontFamily: theme.typography.subFontFamily }}>DIO</Link>
                  </Typography>
                  <Typography sx={{ color: 'text.secondary' }}>내 능력을 가장 잘 활용할 수 있는 사이드잡</Typography>
                </Box>

              </Stack>

              <LoginForm />

              {!smUp && (
                <Typography variant="body2" align="center" sx={{ mt: 3 }}>
                  계정이 없으신가요?{' '}
                  <NextLink href={PATH_AUTH.register} passHref>
                    <Link variant="subtitle2">가입하기</Link>
                  </NextLink>
                </Typography>
              )}
            </ContentStyle>
          </Container>
        </RootStyle>
      </Page>
    </RegisterGuard>
  );
}
